import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { mockQuizApi } from "../../api/mocks/mockQuizApi";
import notDataImage from "../../assets/no-data.svg";

const statusMap = {
  Passed: { color: "bg-green-100 text-green-700", label: "Passed" },
  Failed: { color: "bg-yellow-100 text-yellow-700", label: "Failed" }
};

const QuizHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const courseid = location.pathname.split('/')[3];

  useEffect(() => {
    async function fetchHistory() {
      setLoading(true);
      try {
        const res = await mockQuizApi.getQuizHistory(courseid);
        setHistory(res || []);
      } catch (e) {
        setHistory([]);
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, [courseid]);

  if (loading) return (
    <div className="px-8">
      {[...Array(4)].map((_, idx) => (
        <div key={idx} className="flex items-center justify-between py-6 border-b last:border-b-0">
          <div>
            <div className="mb-1">
              <Skeleton className="h-6 w-32 mb-2" />
            </div>
            <div className="mb-1">
              <Skeleton className="h-4 w-64 mb-2" />
            </div>
            <div className="flex gap-6 text-sm mb-1">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <div className="flex items-center gap-8">
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-32 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="px-8 h-full w-full relative">
      {history.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-10 h-full w-full pb-20">
          <img src={notDataImage} alt="No quiz history found" className="w-[20rem] h-auto" />
          <p className="bg-[var(--primary-font)] text-white py-2 px-6 rounded-md capitalize cursor-default">No quiz history found.</p>
        </div>
      ) : (
        history.map((quiz, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between py-6 border-b last:border-b-0"
          >
            <div className="flex flex-col gap-2">
                <div className="flex gap-6 items-center justify-start mb-2">
                  <div className="text-lg font-bold">{`Quiz ${idx + 1}`}</div>
                  <div className="text-sm text-[#00000080]">{quiz.title}</div>
                  <Badge className={statusMap[quiz.status]?.color || "bg-gray-200 text-gray-700"}>
                    {statusMap[quiz.status]?.label || quiz.status}
                  </Badge>
                </div>
              <div className="flex gap-4 text-sm font-[500] font-helvetica">
                <span className="text-[#47864D]">{quiz.correct ?? 0} Correct</span>
                <span className="text-[#7F2B2C]">{quiz.incorrect ?? 0} Incorrect</span>
                <span className="text-[#A46824]">{quiz.partial ?? 0} Partial</span>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <span className="text-[14px] font-[600] font-helvetica text-[var(--secondary-font)]">Scored {quiz.score ?? 0}/{quiz.totalQuestions ?? 0}</span>
              <Badge
                variant="blue"
                disabled={!quiz.quizId}
                onClick={() => navigate(`/dashboard/courses/${courseid}/test/${quiz.quizId}`)}
                className="cursor-pointer hover:bg-[var(--secondary-font)] hover:text-white rounded-full px-6 py-2 text-md transition-all"
              >
                Retake Quiz
              </Badge>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default QuizHistory;
